import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { apiErrorMessage, formatDay } from "../../lib/utils";

const STATUS_VARIANT = { open: "warning", reviewing: "primary", resolved: "accent", dismissed: "default" };
const FILTERS = [
  { id: "open", label: "Open" },
  { id: "", label: "All" },
];

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () =>
    api
      .get("/admin/content-reports", { params: status ? { status } : {} })
      .then(({ data }) => setReports(data.data || []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const resolve = async (id, decision) => {
    setError(null);
    try {
      await api.post(`/admin/content-reports/${id}/resolve`, { decision });
      load();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not update the report."));
    }
  };

  if (loading) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div>
      <h1 className="mb-2 font-montserrat text-2xl font-bold text-slate-900">Reported reviews</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Reviews customers reported from a business page. "Remove" takes the review down and out of the star rating; "Keep" leaves it up.
        A review reported by 3 different people is hidden until you decide.
      </p>

      <div className="mb-4 flex gap-2">
        {FILTERS.map((f) => (
          <Button key={f.id} size="sm" variant={status === f.id ? "primary" : "outline"} onClick={() => setStatus(f.id)}>
            {f.label}
          </Button>
        ))}
      </div>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <div className="space-y-3">
        {reports.map((r) => (
          <Card key={r.id} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900">
                  {r.business ? <Link to={`/${r.business.slug}`} className="hover:underline">{r.business.name}</Link> : "Deleted business"}
                </p>
                {r.review ? (
                  <>
                    <p className="text-xs text-muted-foreground break-words">
                      {r.review.rating} / 5 by {r.review.customer?.full_name || "a deleted account"}
                      {r.review.hidden_at ? " · hidden" : " · showing"}
                    </p>
                    {r.review.comment && <p className="mt-1 text-sm text-slate-700 break-words">"{r.review.comment}"</p>}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">The review was deleted.</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground break-words">
                  Reported as <span className="font-medium text-slate-700">{r.reason.replace("_", " ")}</span>
                  {" "}by {r.reporter?.full_name || r.reporter_name || "a deleted account"} on {formatDay(r.created_at)}
                </p>
                {r.details && <p className="mt-1 text-sm text-slate-600 break-words">Note: {r.details}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
                {["open", "reviewing"].includes(r.status) && (
                  <>
                    <Button size="sm" onClick={() => resolve(r.id, "remove")}>Remove review</Button>
                    <Button size="sm" variant="outline" onClick={() => resolve(r.id, "keep")}>Keep</Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
        {reports.length === 0 && <p className="text-muted-foreground">{status === "open" ? "No open reports." : "No reports yet."}</p>}
      </div>
    </div>
  );
}
